/* eslint-disable @typescript-eslint/no-namespace */
import { Fluent2WebDarkTheme } from '@fluentui/fluent2-theme';
import type { ITheme } from '@fluentui/react';
import { mergeThemes } from '@fluentui/react';
import type { IM365ExtendedSemanticColors } from '@m365-admin/customizations';

import { getFluent2ComponentStyles } from './fluent2-components-styles';

namespace fluent2DarkPalette {
  // Warnings and Errors
  export const errorText = '#FF3C00';
  export const errorBackground = '#3B1E1E';
  export const blockingBackground = '#3B1E1E';
  export const warningBackground = '#30250A';
  export const warningHighlight = '#FFB900';
  export const successBackground = '#24290F';

  // Extended colors

  export const severeWarningBackground = '#FEE6DD'; // not used, deprecated
  export const severeWarningIcon = '#E36537';

  export const dashboardBackdrop = '#303030';
  export const panelBackground = '#212121';
  export const overlayBackground = 'rgba(0,0,0,.4)';
  export const navBackground = '#212121';

  export const dataVis1 = '#637CEF';
  export const dataVis2 = '#E3008C';
  export const dataVis3 = '#2AA0A4';
  export const dataVis4 = '#9373C0';
  export const dataVis5 = '#13A10E';
  export const dataVis6 = '#3A96DD';
  export const dataVis7 = '#CA5010';
  export const dataVis8 = '#57811B';
  export const dataVis9 = '#B146C2';
  export const dataVis10 = '#AE8C00';

  export const criticalStatus = '#FF0505';
  export const highStatus = '#D33F4C';
  export const mediumStatus = '#F87528';
  export const lowStatus = '#FAA06B';
  export const insightLow = '#4661D5';
  export const insightMedium = '#627CEF';
  export const insightHigh = '#99AAF5';
  export const neutralRisk = '#303030';

  export const goodStatus = '#54B054';
  export const unknownStatus = '#474747';

  export const needHelpBackground = '#00838C';
  export const needHelpBackgroundPressed = '#009BA6';

  export const whiteTranslucent40 = 'rgba(255,255,255,.4)';

  export const tagBackgroundLowImpact = '#323130';
  export const tagBackgroundMediumImpact = '#484644';
  export const tagBackgroundActiveWarning = '#D13438';
  export const tagBackgroundRecommendation = '#0C614C';
  export const tagBackgroundInsight = '#3B30BC';

  export const grey36 = '#5C5C5C';
  export const grey68 = '#ADADAD';
  export const grey80 = '#C8C8C8';

  export const green30 = '#54B054';

  export const red30 = '#E37D80';
}

const p = Fluent2WebDarkTheme.palette;
const m365p = fluent2DarkPalette;

const extendedSemanticColors: Partial<IM365ExtendedSemanticColors> = {
  // Base remapping:
  primaryButtonText: p.black,
  primaryButtonTextHovered: p.black,
  primaryButtonTextPressed: p.black,
  primaryButtonTextDisabled: p.neutralTertiaryAlt,
  primaryButtonBackgroundDisabled: p.neutralLighter,
  accentButtonText: p.black,
  accentButtonBackground: p.themePrimary,
  inputPlaceholderText: p.neutralSecondaryAlt,
  inputForegroundChecked: p.black,
  inputBorder: p.neutralQuaternary,

  // Checkbox
  inputBackgroundChecked: p.themePrimary,
  inputBackground: p.white,
  bodyTextChecked: p.neutralPrimary,
  inputBackgroundCheckedHovered: p.themeDarkAlt,

  // Errors and warnings
  errorText: m365p.errorText,
  warningHighlight: m365p.warningHighlight,
  warningText: p.neutralPrimary,

  // Message bar colors
  messageText: p.neutralPrimary,
  messageLink: p.themeDarkAlt,
  messageLinkHovered: p.themeDarker,

  infoIcon: p.neutralSecondary,
  errorIcon: m365p.highStatus,
  blockingIcon: m365p.highStatus,
  successIcon: m365p.goodStatus,
  warningIcon: p.neutralPrimary,
  severeWarningIcon: m365p.severeWarningIcon,

  infoBackground: p.neutralLighter,
  errorBackground: m365p.errorBackground,
  blockingBackground: m365p.blockingBackground,
  warningBackground: m365p.warningBackground,
  severeWarningBackground: m365p.severeWarningBackground,
  successBackground: m365p.successBackground,

  ////////////////////////////////
  // Extended colors
  ////////////////////////////////

  dashboardBackdrop: m365p.dashboardBackdrop,
  actionCardDashboardBorder: m365p.grey68,
  actionCardDashboardBorderHovered: '#BDBDBD',
  gridItemDestinationBackground: p.neutralQuaternary,
  panelBackground: p.white, // same as bodyBackground in this theme
  overlayBackground: m365p.overlayBackground,

  // Divider lines
  sectionDividerLine: p.neutralPrimary,
  primaryDividerLine: p.neutralTertiaryAlt,
  secondaryDividerLine: p.neutralQuaternaryAlt,

  // Data vis
  dataVis1: m365p.dataVis1,
  dataVis2: m365p.dataVis2,
  dataVis3: m365p.dataVis3,
  dataVis4: m365p.dataVis4,
  dataVis5: m365p.dataVis5,
  dataVis6: m365p.dataVis6,
  dataVis7: m365p.dataVis7,
  dataVis8: m365p.dataVis8,
  dataVis9: m365p.dataVis9,
  dataVis10: m365p.dataVis10,

  // Text
  headerText: p.black,
  secondaryListText: p.neutralSecondary,

  // Nav
  navBackground: m365p.navBackground,
  navItemBackgroundHovered: p.neutralTertiaryAlt,
  navItemBackgroundPressed: p.neutralTertiary,

  // Status
  criticalStatus: m365p.criticalStatus,
  highStatus: m365p.highStatus,
  mediumStatus: m365p.mediumStatus,
  lowStatus: m365p.lowStatus,
  goodStatus: m365p.goodStatus,
  unknownStatus: m365p.unknownStatus,
  insightLow: m365p.insightLow,
  insightMedium: m365p.insightMedium,
  insightHigh: m365p.insightHigh,

  neutralRisk: m365p.neutralRisk,

  // Steps
  stepCompleted: p.themeDark,
  stepCurrent: p.themeDark,
  stepNotStarted: p.neutralSecondaryAlt,
  stepModifierBorder: p.themePrimary,
  stepHover: p.themeDark,
  stepPressed: p.themeDarker,
  stepError: m365p.highStatus,
  allStepsComplete: m365p.goodStatus,

  // Help + Feedback
  needHelpForeground: p.black,
  needHelpBackground: m365p.needHelpBackground,
  needHelpBackgroundPressed: m365p.needHelpBackgroundPressed,
  feedbackForeground: p.black,
  feedbackBackground: p.neutralTertiaryAlt,
  feedbackBackgroundPressed: p.neutralTertiary,

  // Because the Fabric controls bank on theme.palette.white/black inverting.
  alwaysWhite: p.black,
  alwaysBlack: p.white,

  // InPageFilter
  filterPillRest: p.neutralLighter,
  filterPillHover: p.neutralLight,
  filterPillPressed: p.neutralQuaternaryAlt,
  filterPillEditedRest: p.themeLighter,
  filterPillEditedHover: p.themeLight,
  filterPillEditedPressed: p.themeTertiary,

  // Tag
  tagBackgroundInsight: '#2C3C85',
  tagBackgroundLowImpact: '#0A0A0A',
  tagBackgroundMediumImpact: '#666666',
  tagBackgroundHighImpact: p.black, // which is actually white, #FFFFFF
  tagBackgroundNew: '#115EA3',
  tagBackgroundActiveWarning: '#D13438',
  tagBackgroundRecommendation: '#00666D',
  tagTextInsight: p.neutralPrimary,
  tagTextLowImpact: p.neutralPrimary,
  tagTextMediumImpact: p.neutralPrimary,
  tagTextHighImpact: p.white,
  tagTextNew: p.black,
  tagTextActiveWarning: p.black,

  disabledBorder: m365p.grey36,
  disabledText: m365p.grey36,
  inputIconHovered: p.black,
};

export const M365Fluent2DarkTheme: ITheme = mergeThemes(Fluent2WebDarkTheme, {
  semanticColors: extendedSemanticColors,
  components: getFluent2ComponentStyles,
  isInverted: true,
});
